import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Trans } from '@lingui/macro';
import * as Ably from 'ably';
import { AblyProvider } from 'ably/react';
import { ChatClient, LogLevel } from '@ably/chat';
import { ChatClientProvider } from '@ably/chat/react';
import { messagingApi } from '@shared/api/messaging';
import { useAuth } from '@shared/hooks/useAuth';
import type { AblyTokenResponse } from '@jema-manager/shared/types/messaging.types';

interface AblyChatProviderProps {
  children: ReactNode;
  enabled?: boolean;
  /** Stable authorization scope, deliberately separate from live room subscriptions. */
  authorizationRoomIds?: string[];
  cacheScope?: string | null;
}

type AblyConnection = {
  state?: string;
  on: (listener: (stateChange: { current?: string }) => void) => void;
  off?: (listener: (stateChange: { current?: string }) => void) => void;
};

type ClosableAblyRealtime = {
  close?: () => void;
};

type CachedAblyToken = {
  token?: string;
  expiresAtMs?: number;
  pending?: Promise<AblyTokenResponse>;
};

const ABLY_TOKEN_REFRESH_MARGIN_MS = 60_000;
const MAX_EXPLICIT_ABLY_TOKEN_ROOM_IDS = 100;
const ablyTokenCache = new Map<string, CachedAblyToken>();

const buildTokenCacheKey = (cacheScope: string | null | undefined, roomSignature: string) =>
  `${cacheScope ?? 'default'}:${roomSignature || 'all'}`;

const getCachedAblyToken = async (input: {
  roomIds: string[];
  cacheScope?: string | null;
  roomSignature: string;
}) => {
  const cacheKey = buildTokenCacheKey(input.cacheScope, input.roomSignature);
  const cached = ablyTokenCache.get(cacheKey);
  const now = Date.now();

  if (
    cached?.token &&
    cached.expiresAtMs &&
    cached.expiresAtMs - now > ABLY_TOKEN_REFRESH_MARGIN_MS
  ) {
    return cached.token;
  }

  if (cached?.pending) {
    return (await cached.pending).token;
  }

  const tokenRequestRoomIds =
    input.roomIds.length > MAX_EXPLICIT_ABLY_TOKEN_ROOM_IDS ? [] : input.roomIds;
  const pending = messagingApi
    .createAblyToken(tokenRequestRoomIds.length > 0 ? { roomIds: tokenRequestRoomIds } : {})
    .then(token => {
      const expiresAtMs = Date.parse(token.expiresAt);
      if (Number.isFinite(expiresAtMs)) {
        ablyTokenCache.set(cacheKey, {
          token: token.token,
          expiresAtMs,
        });
      } else {
        ablyTokenCache.delete(cacheKey);
      }
      return token;
    })
    .catch(error => {
      ablyTokenCache.delete(cacheKey);
      throw error;
    });

  ablyTokenCache.set(cacheKey, { pending });
  return (await pending).token;
};

export const clearAblyTokenCacheForTests = () => {
  ablyTokenCache.clear();
};

export function AblyChatProvider({
  children,
  enabled = true,
  authorizationRoomIds = [],
  cacheScope = null,
}: AblyChatProviderProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [connectionState, setConnectionState] = useState<string>('initialized');
  const scopedRoomIds = useMemo(
    () => [...new Set(authorizationRoomIds)].sort(),
    [authorizationRoomIds]
  );
  const scopedRoomSignature = scopedRoomIds.join('|');
  const hasAuthenticatedSession = Boolean(isAuthenticated && !isLoading && user?.id);
  const effectiveCacheScope = `${user?.id ?? 'anonymous'}:${cacheScope ?? 'default'}`;
  const previousCacheScopeRef = useRef(effectiveCacheScope);

  useEffect(() => {
    if (previousCacheScopeRef.current === effectiveCacheScope) return;
    // A user/context change must never reuse credentials minted for a previous session.
    ablyTokenCache.clear();
    previousCacheScopeRef.current = effectiveCacheScope;
  }, [effectiveCacheScope]);

  const realtimeClient = useMemo(() => {
    if (!enabled || !hasAuthenticatedSession) return null;
    const authRoomIds = scopedRoomSignature ? scopedRoomSignature.split('|') : [];

    return new Ably.Realtime({
      // Some corporate/mobile networks block WebSockets. Keep the preferred
      // transport first, but allow Ably to fall back to HTTP polling.
      transports: ['web_socket', 'xhr_polling'],
      authCallback: async (_tokenParams, callback) => {
        try {
          const token = await getCachedAblyToken({
            roomIds: authRoomIds,
            cacheScope: effectiveCacheScope,
            roomSignature: scopedRoomSignature,
          });
          callback(null, token);
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          callback(new Ably.ErrorInfo(message, 40170, 401), null);
        }
      },
    });
  }, [effectiveCacheScope, enabled, hasAuthenticatedSession, scopedRoomSignature]);

  const chatClient = useMemo(() => {
    if (!realtimeClient) return null;
    return new ChatClient(realtimeClient, { logLevel: LogLevel.Warn });
  }, [realtimeClient]);

  useEffect(() => {
    if (!realtimeClient) {
      setConnectionState('initialized');
      return;
    }

    const connection = realtimeClient.connection as AblyConnection;
    const listener = (stateChange: { current?: string }) => {
      setConnectionState(stateChange.current ?? connection.state ?? 'unknown');
    };

    setConnectionState(connection.state ?? 'initialized');
    connection.on(listener);

    return () => {
      connection.off?.(listener);
    };
  }, [realtimeClient]);

  useEffect(() => {
    if (!realtimeClient) return;

    return () => {
      // A changed authorization scope creates a replacement client. Close the old
      // transport so it cannot keep retrying its auth callback in the background.
      (realtimeClient as unknown as ClosableAblyRealtime).close?.();
    };
  }, [realtimeClient]);

  const isDegraded = enabled && ['failed', 'suspended', 'disconnected'].includes(connectionState);

  if (!realtimeClient || !chatClient) {
    return <>{children}</>;
  }

  return (
    <AblyProvider client={realtimeClient}>
      <ChatClientProvider client={chatClient}>
        {isDegraded ? (
          <div
            className='border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900'
            role='status'
          >
            <Trans id='features.messages.realtime.degraded'>
              Realtime messaging is reconnecting. Messages will stay on the realtime channel when it
              recovers.
            </Trans>
          </div>
        ) : null}
        {children}
      </ChatClientProvider>
    </AblyProvider>
  );
}
